import React from 'react';

import useForceUpdateOnWindowResize from '../../hooks/useForceUpdateOnWindowResize';

import { ButtonV3 } from '../ButtonV3';
import { LinkV2 } from '../LinkV2';
import Testimonial, { TestimonialProps } from './Testimonial';
import HighlightTitle, { HighlightTitleProps } from './HighlightTitle';

import {
  ButtonsWrapper,
  ImageWrapper,
  MastheadWrapper,
  MastheadLinkWrapper,
  TitleWrapper,
  PropBullet,
  Sidebar,
  SidebarContentWrapper,
  TestimonialWrapper,
  ValueList,
  ValueListItem,
  ValueProp,
} from './Masthead.styles';
import FeaturedImage, { FeaturedImageProps } from './FeaturedImage';
import { ButtonV3Props } from '../ButtonV3/ButtonV3';

interface ValuesProps {
  valueProps?: string[];
}

export const Values: React.FC<ValuesProps> = ({ valueProps: values = [] }) => (
  <ValueList>
    {values.map((prop, index) => (
      <ValueListItem key={`value-prop-${index}`}>
        <PropBullet />
        <ValueProp>{prop}</ValueProp>
      </ValueListItem>
    ))}
  </ValueList>
);

interface DefaultCtaProps {
  btnHref?: string;
  btnOnClick?: () => void;
  btnText?: string;
  secondaryBtnText?: string;
  secondaryBtnHref?: string;
  secondaryBtnOnClick?: () => void;
  secondaryBtnAsLink?: boolean;
  buttonPropOverrides?: {
    primary?: ButtonV3Props;
    secondary?: ButtonV3Props;
  };
}

export const DefaultCtas: React.FC<DefaultCtaProps> = ({
  btnHref,
  btnOnClick,
  btnText,
  secondaryBtnText,
  secondaryBtnOnClick,
  secondaryBtnHref,
  secondaryBtnAsLink = false,
  buttonPropOverrides = {
    primary: {},
    secondary: {},
  },
}) => {
  return (
    <>
      {btnText ? (
        <ButtonV3
          component={btnHref ? 'a' : 'button'}
          href={btnHref || undefined}
          onClick={btnOnClick || undefined}
          size="large"
          {...(buttonPropOverrides?.primary || {})}
        >
          {btnText}
        </ButtonV3>
      ) : null}
      {secondaryBtnText && secondaryBtnHref && (
        <>
          {secondaryBtnAsLink ? (
            <LinkV2 inline href={secondaryBtnHref} onClick={secondaryBtnOnClick || undefined}>
              {secondaryBtnText}
            </LinkV2>
          ) : (
            <ButtonV3
              component="a"
              href={secondaryBtnHref}
              onClick={secondaryBtnOnClick || undefined}
              variant="secondary"
              size="large"
              {...(buttonPropOverrides?.secondary || {})}
            >
              {secondaryBtnText}
            </ButtonV3>
          )}
        </>
      )}
    </>
  );
};

const MastheadTitle: React.FC<{ title: HighlightTitleProps | React.ReactNode }> = ({ title }) => {
  if (!title) return null;

  if (typeof title === 'object' && 'highlightText' in title) {
    return <HighlightTitle {...title} isH1={false} />;
  }

  return <TitleWrapper>{title}</TitleWrapper>;
};

export interface MastheadProps extends DefaultCtaProps, ValuesProps {
  /** Images are eagerly loaded by default unless overriden */
  images?: FeaturedImageProps;
  /** @deprecated will not be displayed */
  productName?: string;
  testimonial?: TestimonialProps;
  /** If passing a react node, you will need to pass with the h1 tag */
  title: HighlightTitleProps | React.ReactNode;
  content?: React.ReactNode;
  actions?: React.ReactNode;
  isPhotoOnRight?: boolean;
  className?: string;
  buttonPropOverrides?: {
    primary?: ButtonV3Props;
    secondary?: ButtonV3Props;
  };
  altMedia?: JSX.Element;
  /** Wraps entire Masthead in a link and removes any internal CTAs */
  asLink?: boolean;
  hideImageNonDesktop?: boolean;
}

export const MastheadMedia = ({
  images,
  altMedia,
}: {
  images?: FeaturedImageProps;
  altMedia?: JSX.Element;
}) => {
  if (altMedia) {
    return altMedia;
  }
  if (images) {
    // Set loading to eager if not set.
    const { background, foreground, foregroundImage } = images;
    if (background?.type === 'image' && !background.parameters.loading) {
      background.parameters.loading = 'eager';
    }
    if (foregroundImage && !foregroundImage.loading) {
      foregroundImage.loading = 'eager';
    }
    if (foreground && !foreground.loading) {
      foreground.loading = 'eager';
    }
    return (
      <FeaturedImage
        foreground={foreground}
        foregroundImage={foregroundImage}
        background={background}
      />
    );
  }
  return null;
};

export const Masthead: React.FC<MastheadProps> = ({
  btnHref,
  btnOnClick,
  btnText,
  secondaryBtnText,
  secondaryBtnHref,
  secondaryBtnOnClick,
  secondaryBtnAsLink = false,
  images,
  testimonial,
  title,
  valueProps,
  content,
  actions,
  isPhotoOnRight,
  className,
  buttonPropOverrides,
  altMedia,
  asLink,
  hideImageNonDesktop = false,
}): JSX.Element => {
  useForceUpdateOnWindowResize();

  const Wrapper = asLink ? MastheadLinkWrapper : MastheadWrapper;
  const wrapperProps = asLink ? { href: btnHref } : {};
  const hasButton =
    (btnHref || btnText || secondaryBtnHref || secondaryBtnText || actions) && !asLink;

  return (
    <Wrapper className={className} {...wrapperProps}>
      <ImageWrapper
        isPhotoOnRight={isPhotoOnRight}
        removePadding={Boolean(altMedia)}
        hideImageNonDesktop={hideImageNonDesktop}
      >
        <MastheadMedia images={images} altMedia={altMedia} />
      </ImageWrapper>
      <Sidebar>
        <SidebarContentWrapper>
          <MastheadTitle title={title} />
          {valueProps ? <Values valueProps={valueProps} /> : null}
          {content}
          {hasButton && (
            <ButtonsWrapper>
              <DefaultCtas
                btnHref={btnHref}
                btnOnClick={btnOnClick}
                btnText={btnText}
                secondaryBtnText={secondaryBtnText}
                secondaryBtnHref={secondaryBtnHref}
                secondaryBtnOnClick={secondaryBtnOnClick}
                secondaryBtnAsLink={secondaryBtnAsLink}
                buttonPropOverrides={buttonPropOverrides}
              />
              {actions}
            </ButtonsWrapper>
          )}
        </SidebarContentWrapper>
      </Sidebar>

      {testimonial && (
        <TestimonialWrapper>
          <Testimonial {...testimonial} />
        </TestimonialWrapper>
      )}
    </Wrapper>
  );
};

export default Masthead;
